import React, { useCallback, useRef, useEffect } from "react";
import i18n from "i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  selectScene,
  addScene,
  removeScene,
  selectScenarioElement,
  updateScenarioElement,
  setInteractiveMode,
  updatePluginToolbar,
} from "../../../common/actions";
import { defaultForScene, emptyScenario } from "../schema";
import VisualTextElement from "./elementTypes/VisualTextElement";
import VisualImageElement from "./elementTypes/VisualImageElement";
import VideoCardElement from "./elementTypes/VideoCardElement";
import FlipCardElement from "./elementTypes/FlipCardElement";
import TrueFalseElement from "./elementTypes/TrueFalseElement";
import MCQElement from "./elementTypes/MCQElement";
import ScenarioPlayer from "./ScenarioPlayer";
import "./elementTypes/elementTypes.scss";
import "./ScenarioEditor.scss";

/* eslint-disable react/prop-types */

const ELEMENT_COMPONENTS = {
  visualtext: VisualTextElement,
  visualimage: VisualImageElement,
  videocard: VideoCardElement,
  flipcard: FlipCardElement,
  truefalse: TrueFalseElement,
  mcq: MCQElement,
};

const TYPE_BADGES = {
  visualtext: {
    label: i18n.t("InteractiveScenario.Badge.VisualText") || "Anlatım & Aksiyon Kartı",
    icon: "article",
  },
  visualimage: {
    label: i18n.t("InteractiveScenario.Badge.VisualImage") || "Görsel Kart",
    icon: "image",
  },
  videocard: {
    label: i18n.t("InteractiveScenario.Badge.VideoCard") || "Video Kart",
    icon: "videocam",
  },
  flipcard: {
    label: i18n.t("InteractiveScenario.Badge.FlipCard") || "Çift Yüzlü Kart",
    icon: "flip",
  },
  truefalse: {
    label: i18n.t("InteractiveScenario.Badge.TrueFalse") || "Doğru / Yanlış",
    icon: "rule",
  },
  mcq: {
    label: i18n.t("InteractiveScenario.Badge.MCQ") || "Çoktan Seçmeli Soru",
    icon: "quiz",
  },
};

const PATTERN_CSS = {
  dots: "radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px) 0 0 / 20px 20px",
  grid: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px) 0 0 / 24px 24px",
  waves:
    "repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(255,255,255,0.05) 6px, rgba(255,255,255,0.05) 12px)",
  none: "none",
};

function DraggableElement({
  element,
  isSelected,
  isHiddenByDefault,
  onSelect,
  onDragEnd,
  onResizeEnd,
  containerRef,
}) {
  const posRef = useRef(null);
  const dragStartRef = useRef(null);
  const hasDraggedRef = useRef(false);
  const sizeRef = useRef(null);
  const resizeStartRef = useRef(null);

  const pos = element.position || { x: 10, y: 10 };
  const size = element.size || { width: 300 };
  const Component = ELEMENT_COMPONENTS[element.type];

  const handlePointerDown = useCallback(
    (e) => {
      if (!containerRef.current) return;
      e.stopPropagation();
      hasDraggedRef.current = false;

      const container = containerRef.current.getBoundingClientRect();
      dragStartRef.current = {
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startPercX: pos.x,
        startPercY: pos.y,
        containerW: container.width,
        containerH: container.height,
      };

      const onMove = (me) => {
        if (!dragStartRef.current) return;
        const { startMouseX, startMouseY, startPercX, startPercY, containerW, containerH } =
          dragStartRef.current;
        const dx = ((me.clientX - startMouseX) / containerW) * 100;
        const dy = ((me.clientY - startMouseY) / containerH) * 100;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) hasDraggedRef.current = true;
        const newX = Math.max(0, Math.min(80, startPercX + dx));
        const newY = Math.max(0, Math.min(85, startPercY + dy));
        posRef.current = { x: Math.round(newX * 10) / 10, y: Math.round(newY * 10) / 10 };
        const node = document.querySelector(`[data-element-id="${element.id}"]`);
        if (node) {
          node.style.left = `${posRef.current.x}%`;
          node.style.top = `${posRef.current.y}%`;
        }
      };

      const onUp = () => {
        if (!hasDraggedRef.current) onSelect();
        if (posRef.current) onDragEnd(posRef.current);
        posRef.current = null;
        dragStartRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [pos, onSelect, onDragEnd, containerRef, element.id]
  );

  const handleResizePointerDown = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      const node = document.querySelector(`[data-element-id="${element.id}"]`);
      if (!node) return;
      const startW = node.offsetWidth;
      const startH = node.offsetHeight;
      resizeStartRef.current = {
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startW,
        startH,
      };

      const onMove = (me) => {
        if (!resizeStartRef.current) return;
        const { startMouseX, startW } = resizeStartRef.current;
        const newW = Math.max(160, startW + (me.clientX - startMouseX));
        sizeRef.current = { width: Math.round(newW) };
        if (node) {
          node.style.width = `${sizeRef.current.width}px`;
        }
      };

      const onUp = () => {
        if (sizeRef.current) onResizeEnd(sizeRef.current);
        sizeRef.current = null;
        resizeStartRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [element.id, onResizeEnd]
  );

  const badge = TYPE_BADGES[element.type];
  const wrapperStyle = {
    left: `${pos.x}%`,
    top: `${pos.y}%`,
    width: size.width ? `${size.width}px` : undefined,
  };

  return (
    <div
      className={`sce-el-wrapper${isSelected ? " sce-el-wrapper--selected" : ""}${isHiddenByDefault ? " sce-el-wrapper--hidden-default" : ""}`}
      data-element-id={element.id}
      style={wrapperStyle}
    >
      {badge && (
        <div className="sce-el-wrapper__badge">
          <span className="material-icons">{badge.icon}</span>
          {badge.label}
          {isHiddenByDefault && (
            <span className="sce-el-wrapper__hidden-badge">
              <span className="material-icons" style={{ fontSize: 11 }}>
                visibility_off
              </span>
              {i18n.t("InteractiveScenario.Label.Hidden") || "Gizli"}
            </span>
          )}
        </div>
      )}
      <div className="sce-el-wrapper__card">
        {Component && <Component element={element} onActions={() => {}} />}
      </div>
      {/* Transparent overlay captures drag/select in design mode */}
      <div className="sce-el-wrapper__overlay" onPointerDown={handlePointerDown} />
      {/* Resize handle — only shown when selected */}
      {isSelected && (
        <div className="sce-el-wrapper__resize-handle" onPointerDown={handleResizePointerDown} />
      )}
    </div>
  );
}

export default function ScenarioEditor({ pluginBoxId }) {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);

  const scenario = useSelector((state) => {
    const present = state.undoGroup?.present || state.undoGroup || {};
    const tb = (present.pluginToolbarsById || {})[pluginBoxId];
    return (tb && tb.state && tb.state.scenario) || null;
  });

  const { selectedSceneId, selectedElementId, mode } = useSelector(
    (state) => state.interactivityStudio || {}
  );

  const boxSelected = useSelector((state) => {
    const present = state.undoGroup?.present || state.undoGroup || {};
    return present.boxSelected || null;
  });

  const handleSelectScene = useCallback(
    (sceneId) => dispatch(selectScene(pluginBoxId, sceneId)),
    [dispatch, pluginBoxId]
  );

  const handleAddScene = useCallback(() => {
    const scene = defaultForScene({
      title: `${i18n.t("scene") || "Sahne"} ${(scenario?.scenes?.length || 0) + 1}`,
    });
    dispatch(addScene(pluginBoxId, scene));
    dispatch(selectScene(pluginBoxId, scene.id));
  }, [dispatch, pluginBoxId, scenario]);

  const handleRemoveScene = useCallback(
    (sceneId) => {
      dispatch(removeScene(pluginBoxId, sceneId));
      dispatch(selectScene(pluginBoxId, null));
    },
    [dispatch, pluginBoxId]
  );

  const handleSelectElement = useCallback(
    (elementId) => {
      dispatch(selectScenarioElement(pluginBoxId, selectedSceneId, elementId));
    },
    [dispatch, pluginBoxId, selectedSceneId]
  );

  const handleElementDragEnd = useCallback(
    (elementId, position) => {
      dispatch(
        updateScenarioElement(pluginBoxId, selectedSceneId, elementId, ["position"], position)
      );
    },
    [dispatch, pluginBoxId, selectedSceneId]
  );

  const handleElementResizeEnd = useCallback(
    (elementId, size) => {
      dispatch(updateScenarioElement(pluginBoxId, selectedSceneId, elementId, ["size"], size));
    },
    [dispatch, pluginBoxId, selectedSceneId]
  );

  const openDesignMode = useCallback(() => {
    dispatch(setInteractiveMode("design"));
    if (scenario?.startSceneId) {
      dispatch(selectScene(pluginBoxId, scenario.startSceneId));
    }
  }, [dispatch, pluginBoxId, scenario]);

  // Auto-select start scene when no scene is selected
  useEffect(() => {
    if (!scenario || selectedSceneId) return;
    if (scenario.startSceneId) {
      dispatch(selectScene(pluginBoxId, scenario.startSceneId));
    }
  }, [scenario, selectedSceneId, dispatch, pluginBoxId]);

  if (!scenario) {
    return (
      <div className="sce-editor sce-editor--empty">
        <span
          className="material-icons"
          style={{ fontSize: 44, color: "#a963f5", marginBottom: 8 }}
        >
          auto_stories
        </span>
        <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: 15, color: "#fff" }}>
          {i18n.t("InteractiveScenario.Empty.Title") || "Senaryo bulunamadı"}
        </p>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
          {i18n.t("InteractiveScenario.Empty.Description") ||
            "Bu element için yeni bir senaryo oluştur"}
        </p>
        <button
          type="button"
          className="sce-editor__ph-btn"
          onClick={() => {
            const sc = emptyScenario();
            dispatch(updatePluginToolbar(pluginBoxId, "main", "state", "scenario", sc, [], null));
            dispatch(setInteractiveMode("design"));
            dispatch(selectScene(pluginBoxId, sc.startSceneId));
          }}
        >
          <span className="material-icons" style={{ fontSize: 14 }}>
            add
          </span>
          {i18n.t("InteractiveScenario.Empty.Create") || "Senaryo Oluştur"}
        </button>
      </div>
    );
  }

  const isGated = window.__ideaGateBoxId === pluginBoxId;
  const isActiveDesign = mode === "design" && boxSelected === pluginBoxId;

  if (mode === "simulate" || isGated) {
    return (
      <div className="sce-editor sce-editor--simulate">
        <ScenarioPlayer key={`${scenario.id}:${scenario.startSceneId}`} scenario={scenario} />
      </div>
    );
  }

  if (!isActiveDesign) {
    const sceneCount = (scenario.scenes || []).length;
    const startScene = (scenario.scenes || []).find((s) => s.id === scenario.startSceneId);
    const bgColor = (startScene && startScene.bgColor) || "#1a1a2e";
    return (
      <div
        className="sce-editor sce-editor--placeholder"
        onDoubleClick={openDesignMode}
        style={{ background: bgColor }}
      >
        <span className="material-icons sce-editor__ph-icon">flash_on</span>
        <span className="sce-editor__ph-label">
          {scenario.name || i18n.t("InteractivePlugin.display_name") || "İnteraktif"} · {sceneCount}{" "}
          {i18n.t("InteractiveScenario.Label.SceneCountSuffix") || "sahne"}
        </span>
        <button
          type="button"
          className="sce-editor__ph-btn"
          onClick={(e) => {
            e.stopPropagation();
            openDesignMode();
          }}
        >
          <span className="material-icons" style={{ fontSize: 13 }}>
            edit
          </span>
          {i18n.t("Edit") || "Düzenle"}
        </button>
      </div>
    );
  }

  const scenes = scenario.scenes || [];
  const selectedScene = scenes.find((s) => s.id === selectedSceneId);

  return (
    <div className="sce-editor sce-editor--canvas-mode">
      {/* Scene tab bar */}
      <div className="sce-editor__scene-tabs">
        {scenes.map((scene) => (
          <button
            key={scene.id}
            type="button"
            className={`sce-editor__scene-tab${scene.id === selectedSceneId ? " sce-editor__scene-tab--active" : ""}${scene.id === scenario.startSceneId ? " sce-editor__scene-tab--start" : ""}`}
            onClick={() => handleSelectScene(scene.id)}
            title={
              scene.id === scenario.startSceneId
                ? `${scene.title} (${i18n.t("InteractiveScenario.Label.StartTitleCase") || "Başlangıç"})`
                : scene.title
            }
          >
            {scene.id === scenario.startSceneId && (
              <span className="material-icons" style={{ fontSize: 11, marginRight: 3 }}>
                play_arrow
              </span>
            )}
            {scene.title || i18n.t("scene") || "Sahne"}
          </button>
        ))}
        <button
          type="button"
          className="sce-editor__scene-tab sce-editor__scene-tab--add"
          onClick={handleAddScene}
          title={i18n.t("InteractiveScenario.Action.AddScene") || "Sahne Ekle"}
        >
          <span className="material-icons">add</span>
        </button>
      </div>

      {/* Scene canvas */}
      {selectedScene ? (
        <div
          className="sce-canvas"
          ref={canvasRef}
          style={{
            background: selectedScene.bgColor || "#1a1a2e",
            backgroundImage:
              selectedScene.bgColor === "transparent"
                ? "repeating-conic-gradient(rgba(0,0,0,0.06) 0% 25%, transparent 0% 50%) 0 0 / 16px 16px"
                : PATTERN_CSS[selectedScene.bgPattern] || "none",
            color: selectedScene.textColor || "#ffffff",
          }}
          onClick={(e) => {
            if (e.target === canvasRef.current) {
              dispatch(selectScenarioElement(pluginBoxId, selectedSceneId, null));
            }
          }}
        >
          {/* Scene label */}
          <div className="sce-canvas__label">
            {selectedScene.title}
            {selectedScene.id === scenario.startSceneId && (
              <span className="sce-canvas__start-badge">
                {`▶ ${i18n.t("InteractiveScenario.Label.StartTitleCase") || "Başlangıç"}`}
              </span>
            )}
          </div>

          {/* Draggable element components */}
          {(selectedScene.elements || []).map((el) => (
            <DraggableElement
              key={el.id}
              element={el}
              isSelected={el.id === selectedElementId}
              isHiddenByDefault={!!el.hidden}
              containerRef={canvasRef}
              onSelect={() => handleSelectElement(el.id)}
              onDragEnd={(pos) => handleElementDragEnd(el.id, pos)}
              onResizeEnd={(size) => handleElementResizeEnd(el.id, size)}
            />
          ))}

          {(selectedScene.elements || []).length === 0 && (
            <div className="sce-canvas__empty">
              <span className="material-icons" style={{ fontSize: 36, color: "#a963f5" }}>
                add_box
              </span>
              <p style={{ margin: "8px 0 4px", fontWeight: 600, color: "#fff", fontSize: 14 }}>
                {i18n.t("InteractiveScenario.Empty.NoElementsInScene") || "Bu sahnede element yok"}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                {i18n.t("InteractiveScenario.Empty.AddElementFromPanel") ||
                  "Sağ panelden element ekle"}
              </p>
            </div>
          )}

          {selectedScene.id !== scenario.startSceneId && (
            <button
              type="button"
              className="sce-canvas__delete-scene"
              onClick={() => handleRemoveScene(selectedScene.id)}
              title={i18n.t("InteractiveScenario.Action.DeleteScene") || "Bu sahneyi sil"}
            >
              <span className="material-icons">delete</span>
            </button>
          )}
        </div>
      ) : (
        <div className="sce-canvas sce-canvas--no-scene">
          <span className="material-icons" style={{ fontSize: 40, color: "#a963f5" }}>
            map
          </span>
          <p style={{ margin: "8px 0 4px", fontWeight: 600, color: "#fff", fontSize: 14 }}>
            {i18n.t("InteractiveScenario.Empty.NoScenesYet") || "Henüz sahne yok"}
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
            {i18n.t("InteractiveScenario.Empty.StartByAddingFirstScene") ||
              "İlk sahneyi ekleyerek başla"}
          </p>
          <button type="button" className="sce-editor__ph-btn" onClick={handleAddScene}>
            <span className="material-icons" style={{ fontSize: 14 }}>
              add
            </span>
            {i18n.t("InteractiveScenario.Action.AddScene") || "Sahne Ekle"}
          </button>
        </div>
      )}
    </div>
  );
}
